'use client';

import type { FeedbackQuestion } from '@up2stay/up2promo-api-client';
import {
  Button,
  Card,
  CardContent,
  cn,
  previewPanelSurfaceClass,
} from '@up2stay/up2promo-ui';

import { FeedbackMailPreview } from '@/core/components/feedback/feedback-mail-preview';
import { FeedbackPhonePreview } from '@/core/components/feedback/feedback-phone-preview';
import {
  FEEDBACK_SECTION_CARD_CLASS,
  FEEDBACK_SECTION_CARD_CONTENT_CLASS,
} from '@/core/components/feedback/feedback-surface';

export type FeedbackComposePreviewTab = 'form' | 'email';

type FeedbackComposePreviewProps = {
  tab: FeedbackComposePreviewTab;
  onTabChange: (tab: FeedbackComposePreviewTab) => void;
  formTitle: string;
  /** The invite's guest hint, shown in the form preview. */
  contextLabel?: string | null;
  questions: FeedbackQuestion[];
  showComment: boolean;
  workspaceName?: string;
  locationName?: string;
  mailSubject: string;
  mailBody: string;
  mailSample: {
    contactName: string;
    locationName: string;
    workspaceName: string;
  };
  logoUrl?: string | null;
  mailCtaLabel?: string;
  poweredByLabel?: string;
  showPoweredBy?: boolean;
  accentColor?: string | null;
  mailAccentColor?: string | null;
  labels: {
    previewForm: string;
    previewEmail: string;
    overallStars: string;
    comment: string;
    commentPlaceholder: string;
    required: string;
    yes: string;
    no: string;
    scaleLow: string;
    scaleHigh: string;
    selectPlaceholder: string;
    submit: string;
    previewNote: string;
    from: string;
    to: string;
  };
};

export function FeedbackComposePreview({
  tab,
  onTabChange,
  formTitle,
  contextLabel,
  questions,
  showComment,
  workspaceName,
  locationName,
  mailSubject,
  mailBody,
  mailSample,
  logoUrl,
  mailCtaLabel,
  poweredByLabel,
  showPoweredBy = true,
  accentColor,
  mailAccentColor,
  labels,
}: FeedbackComposePreviewProps) {
  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex gap-1.5">
        <Button
          type="button"
          size="sm"
          variant={tab === 'form' ? 'ghostAccent' : 'ghost'}
          onClick={() => onTabChange('form')}
        >
          {labels.previewForm}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={tab === 'email' ? 'ghostAccent' : 'ghost'}
          onClick={() => onTabChange('email')}
        >
          {labels.previewEmail}
        </Button>
      </div>
      {tab === 'form' ? (
        <Card
          className={cn(FEEDBACK_SECTION_CARD_CLASS, previewPanelSurfaceClass)}
        >
          <CardContent
            className={cn(
              FEEDBACK_SECTION_CARD_CONTENT_CLASS,
              'flex min-w-0 items-center justify-center overflow-hidden pt-5 sm:pt-6',
            )}
          >
            <FeedbackPhonePreview
              title={formTitle}
              contextLabel={contextLabel}
              workspaceName={workspaceName}
              locationName={locationName}
              logoUrl={logoUrl}
              accentColor={accentColor}
              questions={questions}
              labels={{
                overallStars: labels.overallStars,
                comment: labels.comment,
                commentPlaceholder: labels.commentPlaceholder,
                required: labels.required,
                yes: labels.yes,
                no: labels.no,
                scaleLow: labels.scaleLow,
                scaleHigh: labels.scaleHigh,
                selectPlaceholder: labels.selectPlaceholder,
                submit: labels.submit,
                previewNote: labels.previewNote,
              }}
              showUp2promoBranding={showPoweredBy}
              showComment={showComment}
              className="w-full"
            />
          </CardContent>
        </Card>
      ) : (
        <FeedbackMailPreview
          subject={mailSubject}
          body={mailBody}
          sample={mailSample}
          fromLabel={labels.from}
          toLabel={labels.to}
          logoUrl={logoUrl}
          accentColor={mailAccentColor ?? accentColor}
          ctaLabel={mailCtaLabel}
          poweredByLabel={poweredByLabel}
          showPoweredBy={showPoweredBy}
        />
      )}
    </div>
  );
}
